import React from 'react';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import CardHeader from 'components/Card/CardHeader.js';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import styles from 'assets/jss/material-kit-react/views/profilePage.js';

// Import class images
import amazonClass from 'assets/img/ama.png';
import assassinClass from 'assets/img/assa.png';
import barbarianClass from 'assets/img/barb.png';
import druidClass from 'assets/img/dudu.png';
import necromancerClass from 'assets/img/nec.png';
import paladinClass from 'assets/img/pala.png';
import sorceressClass from 'assets/img/soso.png';
import d2rLogo from 'assets/img/d2r-logo.png';

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  root: {
    width: '100%',
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-4px)',
    },
  },
  cardBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  characterHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  characterImage: {
    width: '48px',
    height: '48px',
    objectFit: 'cover',
  },
  characterName: {
    fontWeight: 600,
    fontSize: '16px',
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '8px 12px',
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',
    borderRadius: '4px',
    border: '1px solid rgba(255, 223, 0, 0.3)',
    fontSize: '12px',
    textDecoration: 'none',
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: 'rgba(255, 223, 0, 0.1)',
      textDecoration: 'none',
    },
  },
}));

const characterClasses = [
  {
    name: 'Paladin',
    image: paladinClass,
    className: 'paladin',
  },
  {
    name: 'Sorceress',
    image: sorceressClass,
    className: 'sorceress',
  },
  {
    name: 'Amazon',
    image: amazonClass,
    className: 'amazon',
  },
  {
    name: 'Druid',
    image: druidClass,
    className: 'druid',
  },
  {
    name: 'Barbarian',
    image: barbarianClass,
    className: 'barbarian',
  },
  {
    name: 'Assassin',
    image: assassinClass,
    className: 'assassin',
  },
  {
    name: 'Necromancer',
    image: necromancerClass,
    className: 'necromancer',
  },
];

export default function ClassesPage() {
  const { t } = useTranslation();
  const classes = useStyles();

  useDocumentTitle(t('navigation.characters'));

  return (
    <div className={classes.mainContainer}>
      <BubbleContainer pageKey="classes" helpText={t('help.classes')} />
      <div className={classes.container}>
        <Card>
          <CardHeader color="primary">
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}
            >
              <img src={d2rLogo} alt="Diablo 2 Resurrected Logo" style={{ height: 36 }} />
              <h4 style={{ margin: 0 }}>{t('navigation.characters')}</h4>
            </div>
          </CardHeader>
          <CardBody>
            <GridContainer spacing={3}>
              {characterClasses.map((character) => (
                <GridItem xs={12} sm={6} md={4} key={character.className}>
                  <Card className={classes.card}>
                    <CardBody className={classes.cardBody}>
                      <div className={classes.characterHeader}>
                        <img
                          src={character.image}
                          alt={character.name}
                          className={classes.characterImage}
                        />
                        <div className={classes.characterName}>{character.name}</div>
                      </div>
                      <div className={classes.actionButtons}>
                        <Link to={`/class/${character.className}`} className={classes.actionButton}>
                          {t('class.details.label')}
                        </Link>
                        <Link
                          to={`/class/${character.className}?tab=guides`}
                          className={classes.actionButton}
                        >
                          {t('class.guides.label')}
                        </Link>
                        <Link
                          to={`/class/${character.className}?tab=packs`}
                          className={classes.actionButton}
                        >
                          {t('class.packs.label')}
                        </Link>
                      </div>
                    </CardBody>
                  </Card>
                </GridItem>
              ))}
            </GridContainer>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
